import {Icon, IconNames} from '@atoms';
import {webIconMap, type GanttEvent, type GanttTrack} from '@lib/store/src/ui/hooks/timeslots/useDaySchedule';
import {minuteOfDayToLocalTimeString, timeLabelFromHhmm} from '@lib/store/src/data/timeslots/dateTimeHelpers';
import {cn} from '@utils';
import {useTranslation} from 'react-i18next';

type GanttChartProps = {
  tracks: Array<GanttTrack>;
  startTime: number;
  endTime: number;
  selectedId: string | undefined;
  invalidIds: Set<string>;
};

export const GanttChart = ({tracks, startTime, endTime, invalidIds, selectedId}: GanttChartProps) => {
  const perc = (x: number) => ((x - startTime) / (endTime - startTime)) * 100;

  return (
    <>
      {tracks.map((t, i) => {
        return (
          <div
            key={i}
            className="flex items-center">
            <Icon
              icon={webIconMap[t.icon]}
              size={'18px'}
              className="mr-2"
            />
            <MinuteTrack
              from={startTime}
              to={endTime}
              blocks={t.events}
              selectedId={selectedId}
              invalidIds={invalidIds}
            />
          </div>
        );
      })}

      {/* Labels */}
      <div className="flex items-center">
        <Icon
          icon="clock"
          size={'18px'}
          color="transparent"
          className="mr-2"
        />
        <div className="w-full h-8 relative">
          {range(startTime, endTime + 120, 120).map((m) => (
            <div
              key={m}
              className="absolute py-2 text-mapiq-black-500"
              style={{
                left: `${perc(m)}%`,
              }}>
              {minuteOfDayToLocalTimeString(m).slice(0, 2)}
            </div>
          ))}
        </div>
      </div>
    </>
  );
};

const MinuteTrack = ({
  from,
  to,
  blocks,
  selectedId,
  invalidIds,
}: {
  from: number;
  to: number;
  blocks: Array<GanttEvent>;
  selectedId: string | undefined;
  invalidIds: Set<string>;
}) => {
  const {t} = useTranslation();
  const perc = (x: number) => ((x - from) / (to - from)) * 100;
  const clamped = blocks
    .map((b) => ({
      ...b,
      from: Math.max(b.from, from),
      to: Math.min(b.to, to),

      trimmedStart: b.from < from,
      trimmedEnd: b.to > to,

      originalFrom: b.from,
      originalTo: b.to,
    }))
    .filter(({from, to}) => to - from > 0);

  return (
    <div className="w-full h-8 relative">
      {/* Grid lines */}
      {range(from, to + 60, 60).map((m) => (
        <div
          key={m}
          className="bg-mapiq-black-150 w-[1px] h-full absolute"
          style={{
            left: `${perc(m)}%`,
          }}
        />
      ))}
      {/* Blocks */}
      {clamped.map((block, i) => {
        const {from: start, to: end, id} = block;
        const timeRange = timeLabelFromHhmm(
          minuteOfDayToLocalTimeString(block.originalFrom),
          minuteOfDayToLocalTimeString(block.originalTo),
          t,
        );
        const title = `[${timeRange}]`;

        const icon: IconNames | null =
          block.type === 'OFFICE_WORKDAY_STATUS'
            ? 'office'
            : block.type === 'REMOTE_WORKDAY_STATUS'
            ? 'remote'
            : block.type === 'NOT_WORKING_WORKDAY_STATUS'
            ? 'calendarX'
            : null;

        return (
          <div
            key={id}
            title={title}
            className={cn(
              `absolute bg-focusing-brown-200 transition-all rounded-md border-1 border-white opacity-80 top-1 bottom-1 flex items-center justify-center`,
              {
                'bg-collaborative-blue-100': block.id === selectedId,
                'bg-[#FBEAE7]': invalidIds.has(block.id),
                'border-functional-red': invalidIds.has(block.id),
                'border-black': block.id === selectedId,
                'border-2': block.id === selectedId || invalidIds.has(block.id),
                'border-dashed': block.id === selectedId,
                'rounded-l-none': block.trimmedStart,
                'rounded-r-none': block.trimmedEnd,
              },
            )}
            style={{
              left: `${perc(start)}%`,
              width: `${perc(end) - perc(start)}%`,
            }}>
            {icon ? (
              <Icon
                size={'14px'}
                icon={icon}
              />
            ) : null}
          </div>
        );
      })}
    </div>
  );
};

function range(from: number, to: number, step = 1): number[] {
  return Array.from({length: (to - from) / step}, (_, i) => from + i * step);
}
